/*
  Copyright (c) Epic Games, Inc. All Rights Reserved.
  CONTAINS AI GENERATED CODE.
*/

import { useEffect, useState } from "react";
import { UAParser } from "ua-parser-js";

/**
 * Shared hook that provides memoized UAParser instance
 * Prevents creating multiple parser instances per component
 */
const useUAParser = (): UAParser | null => {
	const [parser, setParser] = useState<UAParser | null>(null);

	useEffect(() => {
		if (typeof window !== "undefined") {
			setParser(new UAParser());
		}
	}, []);

	return parser;
};

/**
 * Generic hook factory for user agent detection
 * Eliminates code duplication across all detection hooks
 * @param detectionFn - Function that extracts value from UAParser
 * @param defaultValue - Initial value before detection completes
 */
const createUADetectionHook = <T>(detectionFn: (parser: UAParser) => T, defaultValue: T): (() => T) => {
	return (): T => {
		const parser = useUAParser();
		const [value, setValue] = useState<T>(defaultValue);

		useEffect(() => {
			if (parser) {
				setValue(detectionFn(parser));
			}
		}, [parser]);

		return value;
	};
};

/**
 * Returns true if user agent is determined to be a Mac OS device
 */
export const useIsMac = createUADetectionHook((parser) => parser.getOS().name === "Mac OS", false);

/**
 * Returns true if user agent is determined to be a Windows device
 */
export const useIsWindows = createUADetectionHook((parser) => parser.getOS().name === "Windows", false);

/**
 * Returns true if user agent is determined to be a Linux device
 */
export const useIsLinux = createUADetectionHook((parser) => parser.getOS().name === "Linux", false);

/**
 * Returns true if user agent is determined to be a mobile device (phone or tablet)
 */
export const useIsMobile = createUADetectionHook((parser) => {
	const device = parser.getDevice();
	return device.type === "mobile" || device.type === "tablet";
}, false);

/**
 * Returns the full OS information from UAParser
 */
export const useOS = createUADetectionHook((parser) => parser.getOS(), undefined as UAParser.IOS | undefined);

/**
 * Returns the OS name as a lowercase string with no spaces
 */
export const useOsName = (): string => {
	const os = useOS();
	const name = os?.name || "";
	const cleanOS = name.replace(/\s/g, "");
	return cleanOS.toLowerCase();
};

/**
 * Returns true if user agent is determined to be Firefox
 */
export const useIsFirefox = createUADetectionHook((parser) => {
	const browser = parser.getBrowser();
	return (browser.name || "").toLowerCase().includes("firefox");
}, false);

/**
 * Detects Chrome version from user agent
 * Returns 0 if not Chrome or version cannot be determined
 */
const getChromeVersion = (): number => {
	if (typeof window === "undefined") return 0;

	const chromeMatch = window.navigator.userAgent.match(/Chrome\/(\d+)/);
	return chromeMatch ? parseInt(chromeMatch[1], 10) : 0;
};

/**
 * Returns true if Chrome 128 or older
 * Chrome 128 has known performance issues with complex gradients
 */
export const useIsChrome128OrOlder = (): boolean => {
	const [isOldChrome, setIsOldChrome] = useState(false);

	useEffect(() => {
		if (typeof window !== "undefined") {
			const version = getChromeVersion();
			setIsOldChrome(version > 0 && version <= 128);
		}
	}, []);

	return isOldChrome;
};

/**
 * Returns true if running in UnrealEngine embedded browser
 * UnrealEngine embedded browsers may have limited rendering capabilities
 */
export const useIsUnrealEngineContext = (): boolean => {
	const [isUE, setIsUE] = useState(false);

	useEffect(() => {
		if (typeof window !== "undefined") {
			setIsUE(window.navigator.userAgent.includes("UnrealEngine"));
		}
	}, []);

	return isUE;
};

/**
 * Returns true if expensive visual effects should be disabled
 * Based on Chrome 128 detection and UnrealEngine context
 */
export const useDisableExpensiveEffects = (): boolean => {
	const isOldChrome = useIsChrome128OrOlder();
	const isUE = useIsUnrealEngineContext();

	return isOldChrome || isUE;
};

/**
 * Utility: Detects if the browser is Chrome 128 or older (non-hook version)
 * Can be called outside of React components
 */
export const isChrome128OrOlder = (): boolean => {
	const version = getChromeVersion();
	return version > 0 && version <= 128;
};

/**
 * Utility: Detects if running in UnrealEngine context (non-hook version)
 * Can be called outside of React components
 */
export const isUnrealEngineContext = (): boolean => {
	if (typeof window === "undefined") return false;
	return window.navigator.userAgent.includes("UnrealEngine");
};

/**
 * Utility: Determines if expensive visual effects should be disabled (non-hook version)
 * Can be called outside of React components
 */
export const shouldDisableExpensiveEffects = (): boolean => {
	return isChrome128OrOlder() || isUnrealEngineContext();
};
